import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Modal,
  ScrollView,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AssignmentHistory } from '../../types';
import theme from '../../theme';

interface PartnershipViewProps {
  assignment: AssignmentHistory;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onViewForm: (formType: 'receiving' | 'delivery') => void;
  onManageAssignment: () => void;
}

export const PartnershipView: React.FC<PartnershipViewProps> = ({
  assignment,
  isExpanded,
  onToggleExpanded,
  onViewForm,
  onManageAssignment,
}) => {
  const getPartnerCount = () => {
    return assignment.secondaryDriver ? 1 : 0;
  };

  const getFormStatus = () => {
    // Mock form status - in real implementation, check actual form records
    return {
      receiving: { completed: true, bothSigned: !!assignment.secondaryDriver },
      delivery: { completed: assignment.status === 'completed', bothSigned: !!assignment.secondaryDriver },
    };
  };

  const formStatus = getFormStatus();

  if (!isExpanded) {
    // Compact View
    return (
      <TouchableOpacity 
        style={styles.compactContainer}
        onPress={onToggleExpanded}
        activeOpacity={0.7}
      >
        <View style={styles.compactHeader}>
          <View style={styles.vehicleInfo}>
            <View style={styles.vehiclePlateRow}>
              <Text style={styles.vehiclePlate}>{assignment.vehicle?.license_plate}</Text>
              {assignment.secondaryDriver && (
                <View style={styles.sharedBadge}>
                  <Ionicons name="people" size={14} color="#fff" />
                  <Text style={styles.sharedBadgeText}>مشترك / Shared</Text>
                </View>
              )}
            </View>
            <Text style={styles.compactDrivers}>
              {assignment.primaryDriver?.name}
              {assignment.secondaryDriver && (
                <Text style={styles.partnerIndicator}> & {assignment.secondaryDriver.name}</Text>
              )}
            </Text>
          </View>
          
          <View style={styles.compactStatus}>
            {(formStatus.receiving.completed || formStatus.delivery.completed) && (
              <View style={styles.formIndicator}>
                <Ionicons name="document-text" size={16} color={theme.colors.primary} />
                <Text style={styles.formCount}>
                  {(formStatus.receiving.completed ? 1 : 0) + (formStatus.delivery.completed ? 1 : 0)} Forms
                </Text>
              </View>
            )}
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(assignment.status) }]}>
              <Text style={styles.statusText}>{assignment.status}</Text>
            </View>
          </View>
        </View>
        
        <View style={styles.compactDetails}>
          <Text style={styles.vehicleModel}>{assignment.vehicle?.make_model}</Text>
          {assignment.monthly_rent_rate ? (
            <View style={styles.rateBreakdown}>
              <Text style={styles.monthlyRate}>AED {assignment.monthly_rent_rate}/mo</Text>
              <Text style={styles.dailyRateSmall}>(AED {assignment.daily_rate}/day)</Text>
            </View>
          ) : (
            <Text style={styles.dailyRate}>AED {assignment.daily_rate}/day</Text>
          )}
          <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
        </View>
      </TouchableOpacity>
    );
  }

  // Expanded View
  return (
    <View style={styles.expandedContainer}>
      <TouchableOpacity 
        style={styles.expandedHeader}
        onPress={onToggleExpanded}
        activeOpacity={0.7}
      >
        <View style={styles.headerLeft}>
          <Text style={styles.expandedTitle}>Partnership Assignment</Text>
          <Text style={styles.vehiclePlate}>{assignment.vehicle?.license_plate}</Text>
        </View>
        <Ionicons name="chevron-up" size={24} color={theme.colors.textSecondary} />
      </TouchableOpacity>

      <View style={styles.driversSection}>
        {/* Primary Driver */}
        <View style={styles.driverCard}>
          <View style={styles.driverHeader}>
            <Ionicons name="person" size={20} color={theme.colors.primary} />
            <Text style={styles.driverRole}>Primary Driver</Text>
          </View>
          <Text style={styles.driverName}>{assignment.primaryDriver?.name || 'Unknown'}</Text>
          <Text style={styles.driverPhone}>{assignment.primaryDriver?.phone || 'No phone'}</Text>
        </View>

        {/* Secondary Driver */}
        {assignment.secondaryDriver && (
          <View style={[styles.driverCard, styles.secondaryDriverCard]}>
            <View style={styles.driverHeader}>
              <Ionicons name="people" size={20} color={theme.colors.success} />
              <Text style={[styles.driverRole, styles.secondaryDriverRole]}>Partner Driver</Text>
            </View>
            <Text style={styles.driverName}>{assignment.secondaryDriver.name}</Text>
            <Text style={styles.driverPhone}>{assignment.secondaryDriver.phone || 'No phone'}</Text>
          </View>
        )}
      </View>

      {/* Vehicle Details */}
      <View style={styles.vehicleSection}>
        <Text style={styles.sectionTitle}>Vehicle Details</Text>
        <Text style={styles.vehicleDetail}>{assignment.vehicle?.make_model}</Text>
        <Text style={styles.vehicleDetail}>Daily Rate: AED {assignment.daily_rate}</Text>
        <Text style={styles.vehicleDetail}>Assignment Date: {new Date(assignment.assignment_date).toLocaleDateString()}</Text>
      </View>

      {/* Forms & Documents */}
      <View style={styles.formsSection}>
        <Text style={styles.sectionTitle}>📋 Forms & Documents</Text>
        
        {/* Delivery Form - Always available (created at assignment START) */}
        <TouchableOpacity 
          style={styles.formItem}
          onPress={() => onViewForm('delivery')}
        >
          <View style={styles.formLeft}>
            <Ionicons name="car" size={24} color={theme.colors.primary} />
            <View style={styles.formDetails}>
              <Text style={styles.formTitle}>Vehicle Delivery Form</Text>
              <Text style={styles.formDate}>
                {new Date(assignment.assignment_date).toLocaleDateString()}
              </Text>
            </View>
          </View>
          <View style={styles.signatureStatus}>
            <View style={styles.signatureBadge}>
              <Text style={styles.signatureText}>✅ Primary</Text>
            </View>
            {assignment.secondaryDriver && (
              <View style={styles.signatureBadge}>
                <Text style={styles.signatureText}>✅ Partner</Text>
              </View>
            )}
          </View>
        </TouchableOpacity>

        {/* Receiving Form - Only available for completed assignments (created at assignment END) */}
        {assignment.status !== 'active' && (
          <TouchableOpacity 
            style={styles.formItem}
            onPress={() => onViewForm('receiving')}
          >
            <View style={styles.formLeft}>
              <Ionicons name="document-text" size={24} color={theme.colors.primary} />
              <View style={styles.formDetails}>
                <Text style={styles.formTitle}>Vehicle Receiving Form</Text>
                <Text style={styles.formDate}>
                  {assignment.return_date ? new Date(assignment.return_date).toLocaleDateString() : 'Completed'}
                </Text>
              </View>
            </View>
            <View style={styles.signatureStatus}>
              {formStatus.receiving.completed && (
                <>
                  <View style={styles.signatureBadge}>
                    <Text style={styles.signatureText}>✅ Primary</Text>
                  </View>
                  {assignment.secondaryDriver && formStatus.receiving.bothSigned && (
                    <View style={styles.signatureBadge}>
                      <Text style={styles.signatureText}>✅ Partner</Text>
                    </View>
                  )}
                </>
              )}
            </View>
          </TouchableOpacity>
        )}
      </View>

      {/* Action Buttons */}
      <View style={styles.actionsSection}>
        <TouchableOpacity 
          style={[styles.actionButton, styles.primaryButton]}
          onPress={() => onViewForm('receiving')}
        >
          <Ionicons name="document-text" size={18} color="white" />
          <Text style={styles.actionButtonText}>View Forms</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={[styles.actionButton, styles.secondaryButton]}
          onPress={onManageAssignment}
        >
          <Ionicons name="settings" size={18} color={theme.colors.primary} />
          <Text style={[styles.actionButtonText, styles.secondaryButtonText]}>Manage</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return theme.colors.success;
    case 'completed': return theme.colors.textSecondary;
    case 'cancelled': return theme.colors.error;
    default: return theme.colors.textSecondary;
  }
};

const styles = StyleSheet.create({
  // Compact View Styles
  compactContainer: {
    backgroundColor: 'white',
    marginHorizontal: theme.spacing.md,
    marginVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  compactHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing.sm,
  },
  vehicleInfo: {
    flex: 1,
  },
  vehiclePlateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  vehiclePlate: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  sharedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    gap: 4,
  },
  sharedBadgeText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '600',
  },
  compactDrivers: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  partnerIndicator: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  compactStatus: {
    alignItems: 'flex-end',
  },
  formIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  formCount: {
    fontSize: 12,
    color: theme.colors.primary,
    marginLeft: 4,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    color: 'white',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  compactDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  vehicleModel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    flex: 1,
  },
  dailyRate: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.success,
    marginRight: theme.spacing.sm,
  },

  // Expanded View Styles
  expandedContainer: {
    backgroundColor: 'white',
    marginHorizontal: theme.spacing.md,
    marginVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
  expandedHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  headerLeft: {
    flex: 1,
  },
  expandedTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.primary,
    marginBottom: 2,
  },

  // Drivers Section
  driversSection: {
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  driverCard: {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.primary,
  },
  secondaryDriverCard: {
    borderLeftColor: theme.colors.success,
    backgroundColor: '#f0f9ff',
  },
  driverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  driverRole: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.primary,
    marginLeft: theme.spacing.xs,
  },
  secondaryDriverRole: {
    color: theme.colors.success,
  },
  driverName: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  driverPhone: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },

  // Vehicle Section
  vehicleSection: {
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
  },
  vehicleDetail: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },

  // Forms Section
  formsSection: {
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  formItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  formLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  formDetails: {
    marginLeft: theme.spacing.md,
    flex: 1,
  },
  formTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  formTitleDisabled: {
    color: theme.colors.textSecondary,
  },
  formDate: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  signatureStatus: {
    alignItems: 'flex-end',
  },
  signatureBadge: {
    backgroundColor: '#dcfce7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 2,
  },
  signatureText: {
    fontSize: 12,
    color: '#166534',
    fontWeight: '500',
  },

  // Actions Section
  actionsSection: {
    flexDirection: 'row',
    padding: theme.spacing.md,
    gap: theme.spacing.md,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  primaryButton: {
    backgroundColor: theme.colors.primary,
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: 'white',
  },
  secondaryButtonText: {
    color: theme.colors.primary,
  },
});